// Local libary
import { FundingFacade } from '@amwager/funding/data-access';
import { UnsubscribeObservableService } from '@amwager/util';

// Angular libray
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { AlertController } from '@ionic/angular/standalone';

// Third party
import { Subscription, takeUntil } from 'rxjs';
import { forEach } from 'lodash';
import { CommonModule } from '@angular/common';
import { CommonFacade } from '@amwager/data-access';
import { AuthFacade } from '@amwager/authentication/data-access';


@Component({
  selector: 'feature-ion-promo-code',
  templateUrl: './promo-code.component.html',
  styleUrls: ['./promo-code.component.scss'],
  imports: [
    CommonModule,
    ReactiveFormsModule,
  ],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PromoCodeComponent extends UnsubscribeObservableService implements OnInit {

  promoCodeForm!: FormGroup;

  // convenience getter for easy access to form fields
  get formVal() { return this.promoCodeForm?.controls }

  promoCode!: string;

  private subsPromoCodeResponse!: Subscription;

  isDepositing = false;

  private showAlertMessageHandler?: HTMLIonAlertElement;

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly fundingFacade: FundingFacade,
    private readonly alertCtrl: AlertController,
    private readonly cdRef: ChangeDetectorRef,
    private readonly commonFacade: CommonFacade,
    private readonly authFacade: AuthFacade,
  ) {
    super();

    this.authFacade.isLogoutSuccess$().subscribe((isLogout?: boolean) => {
      if (isLogout) {
        this.alertCtrl?.dismiss();
        this.showAlertMessageHandler?.dismiss();
      }
    });
  }

  ngOnInit() {
    // update branch
    this.initForm();
    this.formVal['promoCode']?.setValue('');
    this.promoCode = '';
  }

  private initForm(): void {
    this.promoCodeForm = this.formBuilder.group({
      promoCode: [
        '', [Validators.required]
      ]
    });
  }

  promoCodeValue(): void {
    this.promoCode = this.formVal['promoCode']?.value;
  }

  deposit(): void {
    if (!navigator.onLine) {
      this.commonFacade.setIsConnectedInternet('PromoCodeComponent:deposit', false);
      return;
    }

    if (this.promoCodeForm.valid && this.promoCode) {
      this.isDepositing = true;

      this.fundingFacade.depositPromoCode(
        this.promoCode,
      );
      this.subsPromoCodeResponse = this.fundingFacade.isDepositPromoCodeSuccess$()
      .pipe(takeUntil(this.toUnsubscribe$))
      .subscribe(({response, isSuccess}) => {
        if (!isSuccess && response !== undefined) {
          const message = response.error.Error === 'User role not authorized for this action.' ?
            'You are not an Identified User.' : response.error.Message;
          this.showAlertMessage('Deposit Error', message);
          this.isDepositing = false;
          this.cdRef.markForCheck();
        }

        this.subsPromoCodeResponse?.unsubscribe();

        if (isSuccess) {
          this.showAlertMessage('Successful Deposit', 'Successfully Deposited Amount');
          this.promoCodeForm.reset();
          this.isDepositing = false;
          this.cdRef.markForCheck();
        }

      });
    }
    else {
      this.markFormGroupTouched(this.promoCodeForm);
    }
  }

  markFormGroupTouched(formGroup: FormGroup): void {
    forEach(formGroup.controls, (control) => {
      control.markAsTouched();
      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }

  async showAlertMessage(header: string, message: string): Promise<void> {
    this.showAlertMessageHandler = await this.alertCtrl.create({
      header,
      message: message,
      buttons: [
        {
          text: 'Close',
          role: 'cancel',
          handler: () => {
          }
        }
      ]
    });

    await this.showAlertMessageHandler.present();
  }

}
